import { AxiosResponse } from 'axios';

import { supabase } from '@/lib/supabase';
import { asResponse } from '@/lib/supabase-helpers';

type UserParams = {
  candidateId: string;
};

export type DeleteEducationType = UserParams & {
  id: string;
};

export type NewEducationType = UserParams & {
  university: string;
  degree: string;
  duration: string;
  fromMonth: string;
  fromYear: string;
  startMonth: string;
  toYear: string;
  currentlyPursuing: boolean;
};

export type UpdateEducationType = NewEducationType & {
  id: string;
};

type CommonType = {
  updatedAt: string;
  id: string;
  createdAt: string;
};

type Skill = CommonType & {
  name: string;
};

type SkillsType = CommonType & {
  updatedAt: string;
  id: string;
  createdAt: string;
  skill: Skill;
};

export type EducationType = {
  id: string;
  createdAt: string;
  description: string;
  link: string;
  skills: SkillsType[];
  title: string;
  updatedAt: string;
  year: string;
};

export type EducationResponseType = {
  projects: EducationType[];
};

function buildFromDate(fromMonth: string, fromYear: string): string | null {
  if (!fromMonth || !fromYear) return null;
  return `${fromYear}-${fromMonth.padStart(2, '0')}-01`;
}

function buildToDate(toYear: string, startMonth: string): string | null {
  if (!toYear) return null;
  const month = startMonth || '01';
  return `${toYear}-${month.padStart(2, '0')}-01`;
}

const getAllEducation = async ({
  candidateId,
}: UserParams): Promise<AxiosResponse<EducationResponseType>> => {
  const { data, error } = await supabase
    .from('educations')
    .select('*')
    .eq('profile_id', candidateId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  const projects = (data || []).map((row: any) => ({
    id: row.id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    description: row.description || '',
    link: '',
    skills: [],
    title: row.institution || '',
    year: row.from_date || '',
  }));

  return asResponse({ projects }) as unknown as AxiosResponse<EducationResponseType>;
};

const createNewEducation = async ({ candidateId, ...education }: NewEducationType): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('educations')
    .insert({
      profile_id: candidateId,
      institution: education.university,
      degree: education.degree,
      field_of_study: education.duration,
      from_date: buildFromDate(education.fromMonth, education.fromYear),
      to_date: buildToDate(education.toYear, education.startMonth),
      is_current: education.currentlyPursuing,
    })
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const updateEducation = async ({
  candidateId,
  id,
  ...education
}: UpdateEducationType): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('educations')
    .update({
      institution: education.university,
      degree: education.degree,
      field_of_study: education.duration,
      from_date: buildFromDate(education.fromMonth, education.fromYear),
      to_date: buildToDate(education.toYear, education.startMonth),
      is_current: education.currentlyPursuing,
    })
    .eq('id', id)
    .eq('profile_id', candidateId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const deleteEducation = async ({ candidateId, id }: DeleteEducationType): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('educations')
    .delete()
    .eq('id', id)
    .eq('profile_id', candidateId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

export default {
  getAllEducation,
  createNewEducation,
  updateEducation,
  deleteEducation,
};
